/* 
  DROP-IN REPLACEMENT for the toggleChat button in Chatbot.jsx
  Replace the existing <button onClick={toggleChat} ...> block with this component.
  
  DEPENDENCIES: useTheme hook (already in your project)
  USAGE: import ChatButton from './ChatButton'; then use <ChatButton onClick={toggleChat} />
*/

import React from 'react';
import { useTheme } from '../hooks/useTheme';

const ChatButton = ({ onClick }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <>
      <div
        className="chat-fab-wrapper"
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label="Open chat with Acy"
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        {/* Spinning glow ring */}
        <div className="chat-fab-glow" />
        {/* Soft halo blur layer */}
        <div className={`chat-fab-glow chat-fab-glow-blur ${isDark ? 'glow-dark' : 'glow-light'}`} />
        {/* Glass button body */}
        <div className={`chat-fab-body ${isDark ? 'fab-dark' : 'fab-light'}`}>
          {/* Logo circle */}
          <div className={`fab-logo-wrap ${isDark ? 'logo-dark' : 'logo-light'}`}>
            <img
              src="/Acespire-logo.png"
              alt="Acespire"
              className="fab-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            {/* Fallback if logo doesn't load */}
            <div className="fab-logo-fallback" style={{ display: 'none' }}>A</div>
          </div>
          <span className={`fab-label ${isDark ? 'label-dark' : 'label-light'}`}>Chat</span>
        </div>
        {/* Green online pulse dot */}
        {/* <span className="fab-pulse-dot" aria-hidden="true" /> */}
      </div>

      <style>{`
        /* ── Wrapper ── */
        .chat-fab-wrapper {
          position: fixed;
          bottom: 20px;
          right: 24px;
          width: 120px;
          height: 46px;
          z-index: 50;
          cursor: pointer;
          outline: none;
        }

        /* ── Spinning glow ring (anticlockwise) ── */
        @property --fab-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }

        @keyframes fab-spin {
          from { --fab-angle: 360deg; }
          to   { --fab-angle: 0deg;   }
        }

        .chat-fab-glow {
          position: absolute;
          inset: -3px;
          border-radius: 30px;
          background: conic-gradient(
            from var(--fab-angle, 0deg),
            transparent    0deg,
            #60b4ff       50deg,
            #195CA2      120deg,
            #93d0ff      190deg,
            transparent  260deg,
            transparent  360deg
          );
          animation: fab-spin 2.6s linear infinite;
          z-index: 0;
        }

        /* Override for dark mode glow colours */
        .glow-dark {
          background: conic-gradient(
            from var(--fab-angle, 0deg),
            transparent    0deg,
            #7dd3fc       50deg,
            #3b9ddd      120deg,
            #bae6fd      190deg,
            transparent  260deg,
            transparent  360deg
          ) !important;
        }

        /* Blur halo layer */
        .chat-fab-glow-blur {
          inset: -6px !important;
          border-radius: 34px !important;
          filter: blur(9px) !important;
          opacity: 0.5 !important;
          z-index: 0 !important;
        }

        /* ── Glass body ── */
        .chat-fab-body {
          position: absolute;
          inset: 2px;
          border-radius: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          z-index: 2;
          transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);
          overflow: hidden;
          user-select: none;
        }

        .chat-fab-wrapper:hover  .chat-fab-body { transform: scale(1.05); }
        .chat-fab-wrapper:active .chat-fab-body { transform: scale(0.96); }

        /* Light glass */
        .fab-light {
          background: rgba(255, 255, 255, 0.72);
          backdrop-filter: blur(18px) saturate(1.5);
          -webkit-backdrop-filter: blur(18px) saturate(1.5);
          border: 0.5px solid rgba(255, 255, 255, 0.92);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.85),
            0 4px 20px rgba(25,92,162,0.15);
        }

        /* Dark glass */
        .fab-dark {
          background: rgba(10, 20, 42, 0.78);
          backdrop-filter: blur(18px) saturate(1.7);
          -webkit-backdrop-filter: blur(18px) saturate(1.7);
          border: 0.5px solid rgba(100, 180, 255, 0.22);
          box-shadow:
            inset 0 1px 0 rgba(140,200,255,0.18),
            0 4px 20px rgba(25,92,162,0.3);
        }

        /* ── Logo ring ── */
        .fab-logo-wrap {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .logo-light {
          background: rgba(25, 92, 162, 0.1);
          box-shadow: 0 0 0 1.5px rgba(25, 92, 162, 0.28);
        }

        .logo-dark {
          background: rgba(100, 190, 255, 0.14);
          box-shadow: 0 0 0 1.5px rgba(100, 190, 255, 0.32);
        }

        .fab-logo-img {
          width: 17px;
          height: 17px;
          border-radius: 50%;
          object-fit: contain;
        }

        .fab-logo-fallback {
          width: 17px;
          height: 17px;
          border-radius: 50%;
          background: #195CA2;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          font-weight: 700;
          color: #fff;
        }

        /* ── Label ── */
        .fab-label {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.01em;
          white-space: nowrap;
        }

        .label-light { color: #195CA2; }
        .label-dark  { color: #a8d8ff; }

        /* ── Online pulse dot ── */
        .fab-pulse-dot {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #22c55e;
          z-index: 3;
          animation: fab-pulse 1.9s ease-out infinite;
        }

        @keyframes fab-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(34, 197, 94, 0.55); }
          70%  { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0);    }
          100% { box-shadow: 0 0 0 0   rgba(34, 197, 94, 0);    }
        }

        /* ── Responsive: hide label on very small screens ── */
        @media (max-width: 400px) {
          .chat-fab-wrapper {
            width: 46px;
          }
          .fab-label {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default ChatButton;